import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';

import { useGetGuardians } from '../../hooks/guardians/useGetGuardians';
import { useDeleteGuardian } from '../../hooks/guardians/useDeleteGuardian';
import { useDeleteAllGuardians } from '../../hooks/guardians/useDeleteAllGuardians';
import { useForceDeleteGuardian } from '../../hooks/guardians/useForceDeleteGuardian';
import { useForceDeleteAllGuardians } from '../../hooks/guardians/useForceDeleteAllGuardians';
import { useRestoreGuardian } from '../../hooks/guardians/useRestoreGuardian';
import { useRestoreAllGuardians } from '../../hooks/guardians/useRestoreAllGuardians';
import { usePermissions } from '../../auth/PermissionsContext';

import useTableActions from '../../hooks/common/useTableActions';
import TableLayout from '../global/Table/TableLayout';
import ArchiveButton from '../global/Table/ArchiveButton';
import GuardiansTableColumns from './Table/GuardiansTableColumns';
import GuardiansTableRow from './Table/GuardiansTableRow';

const GuardiansTable = ({ isArchive = false }) => {
    const { i18n } = useTranslation();
    const isArabic = i18n.language === 'ar';
    const navigate = useNavigate();
    const { has } = usePermissions();

    const perms = useMemo(() => ({
        list: has('guardians_list'), create: has('guardians_create'), edit: has('guardians_edit'),
        delete: has('guardians_delete'), archive_list: has('guardians_archive_list'),
        restore: has('guardians_restore'), force_delete: has('guardians_force_delete'),
    }), [has]);

    const { deleteGuardian, isDelete } = useDeleteGuardian();
    const { deleteAllGuardians, isDelete: isDeletingAll } = useDeleteAllGuardians();
    const { forceDeleteGuardian } = useForceDeleteGuardian();
    const { forceDeleteAllGuardians } = useForceDeleteAllGuardians();
    const { restoreGuardian } = useRestoreGuardian();
    const { restoreAllGuardians } = useRestoreAllGuardians();

    const {
        page, perPage, search, sortBy, sortDir, selectedIds, menuAnchorEl,
        setSearch, handleRequestSort, handleRowCheckboxClick, handleSelectAllClick,
        handleMenuOpen, handleMenuClose, handleEdit, handleMenuDelete, handleMenuRestore,
        handleBulkDelete, handleBulkRestore, handlePageChange, handlePerPageChange,
    } = useTableActions({
        basePath: '/guardians', deleteSingle: deleteGuardian, deleteAll: deleteAllGuardians,
        forceDeleteSingle: forceDeleteGuardian, forceDeleteAll: forceDeleteAllGuardians,
        restoreSingle: restoreGuardian, restoreAll: restoreAllGuardians, isArchive,
    });

    const filters = useMemo(() => ({
        search: search || undefined, sort_by: sortBy || undefined, sort_dir: sortDir || undefined,
        ...(isArchive ? { only_trashed: 1 } : {}),
    }), [search, sortBy, sortDir, isArchive]);

    const { isLoading, error, guardians, meta } = useGetGuardians(page, perPage, filters);

    const renderRow = (params) => (
        <GuardiansTableRow key={params.key || params.item.id} item={params.item} index={params.index}
            isArabic={params.isArabic} isDark={params.isDark} page={params.page} perPage={params.perPage}
            order={params.order} totalItems={guardians.length} selectedIds={params.selectedIds}
            onRowCheckboxClick={params.onRowCheckboxClick} onMenuOpen={params.onMenuOpen} />
    );

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ marginInlineStart: 'auto' }}>
                    {!isArchive && perms.archive_list && <ArchiveButton to="/guardians/archive" />}
                    {isArchive && perms.list && <ArchiveButton to="/guardians" archiveLabelAr="العودة للقائمة" archiveLabelEn="Back to List" />}
                </Box>
            </Box>

            <TableLayout
                title={isArabic ? (isArchive ? 'أرشيف أولياء الأمور' : 'أولياء الأمور') : (isArchive ? 'Guardians Archive' : 'Guardians')}
                subtitle={isArabic ? (isArchive ? 'إدارة أولياء الأمور المحذوفين' : 'إدارة أولياء الأمور') : (isArchive ? 'Manage deleted guardians' : 'Manage guardians')}
                search={search} onSearchChange={setSearch}
                addButtonLabel={isArabic ? 'إضافة ولي أمر' : 'Add Guardian'}
                onAddClick={() => navigate('/guardians/create')} addButtonVisible={!isArchive && perms.create}
                isLoading={isLoading} error={error} items={guardians} meta={meta}
                page={page} perPage={perPage} onPageChange={handlePageChange} onPerPageChange={handlePerPageChange}
                sortBy={sortBy} sortDir={sortDir} onRequestSort={handleRequestSort}
                selectedIds={selectedIds} onSelectAllClick={(e) => handleSelectAllClick(e, guardians)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete} onBulkRestore={isArchive && perms.restore ? handleBulkRestore : undefined}
                isBulkDeleting={isDeletingAll}
                columns={GuardiansTableColumns} renderRow={renderRow}
                menuAnchorEl={menuAnchorEl} openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen} onMenuClose={handleMenuClose}
                onMenuEdit={!isArchive && perms.edit ? handleEdit : undefined}
                onMenuDelete={isArchive ? (perms.force_delete ? handleMenuDelete : undefined) : (perms.delete ? handleMenuDelete : undefined)}
                onMenuRestore={isArchive && perms.restore ? handleMenuRestore : undefined}
                menuDisableEdit={isArchive || !perms.edit} menuDisableDelete={isArchive ? !perms.force_delete : !perms.delete}
                menuDisableRestore={!isArchive || !perms.restore} isDeletingSingle={isDelete}
                emptyLabelAr={isArchive ? 'لا يوجد أولياء أمور في الأرشيف' : 'لا يوجد أولياء أمور'}
                emptyLabelEn={isArchive ? 'No archived guardians' : 'No guardians'}
                loadErrorAr="حدث خطأ أثناء تحميل أولياء الأمور" loadErrorEn="Failed to load guardians"
            />
        </>
    );
};

export default GuardiansTable;
